/**
 * Global defaults for XaitCharts (AngularJS) — the `xaChartsConfig` provider.
 *
 * Hosts configure defaults at config time via `xaChartsConfigProvider`
 * (mirrors the Angular package's `provideXaCharts({ palette })`):
 *
 * ```js
 * angular.module('app', ['xait.charts']).config(['xaChartsConfigProvider',
 *   function (xaChartsConfigProvider) {
 *     xaChartsConfigProvider.setDefaults({ palette: 'vivid' });
 *   }]);
 * ```
 *
 * Chart components inject the `xaChartsConfig` service and use its `palette`
 * when their `colors` binding is unset. Resolution order per instance:
 * explicit `colors` binding → global `palette` default → per-chart spec
 * default (`'tokens'` bars, `'brand'` donut/line/area, `'mono'` pie).
 */
import type { XaPalette } from '@xait/charts-core';

/** Global default overrides shared by every chart instance. */
export interface XaChartsDefaults {
  /** Default palette for charts that have no explicit `colors` binding. */
  palette?: XaPalette;
}

/** Provider registered as `xaChartsConfig` (injectable: `xaChartsConfigProvider`). */
export class XaChartsConfigProvider implements ng.IServiceProvider {
  private defaults: XaChartsDefaults = {};

  /** Merge global defaults (call from a `.config()` block). */
  setDefaults(defaults: XaChartsDefaults): void {
    this.defaults = { ...this.defaults, ...defaults };
  }

  /** The runtime `xaChartsConfig` service: the resolved defaults object. */
  $get(): XaChartsDefaults {
    return this.defaults;
  }
}
